import React, { useEffect, useState } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

export interface ImageViewerImage {
  localPath: string;
  alt?: string;
}

interface ImageViewerModalProps {
  open: boolean;
  images: ImageViewerImage[];
  initialIndex?: number;
  onClose: () => void;
}

/**
 * Full-screen image viewer that stacks on top of the existing BidetModal.
 *
 * Uses a higher z-index than the underlying Modal (z-[60] vs z-50) and does NOT
 * lock body scroll — the parent Modal already has it locked, and re-locking
 * here would break the un-lock when this child closes.
 */
export const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  open,
  images,
  initialIndex = 0,
  onClose,
}) => {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % images.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, images.length, onClose]);

  if (!open || images.length === 0) return null;

  const current = images[index];
  const hasMultiple = images.length > 1;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      {/* Close button */}
      <button
        type="button"
        aria-label="Close image viewer"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 z-10 text-white bg-black/40 hover:bg-black/60 rounded-full w-12 h-12 flex items-center justify-center"
      >
        <FiX className="w-7 h-7" />
      </button>

      {/* Image counter */}
      {hasMultiple && (
        <div className="absolute top-4 left-4 z-10 text-white bg-black/40 rounded-full px-4 py-2 text-sm">
          {index + 1} / {images.length}
        </div>
      )}

      {/* Prev / Next buttons */}
      {hasMultiple && (
        <>
          <button
            type="button"
            aria-label="Previous image"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white bg-black/40 hover:bg-black/60 rounded-full w-12 h-12 flex items-center justify-center"
          >
            <FiChevronLeft className="w-7 h-7" />
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i + 1) % images.length);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white bg-black/40 hover:bg-black/60 rounded-full w-12 h-12 flex items-center justify-center"
          >
            <FiChevronRight className="w-7 h-7" />
          </button>
        </>
      )}

      {/* Main image */}
      <img
        src={current.localPath.replace(/^public\//, '/')}
        alt={current.alt || `Product image ${index + 1}`}
        className="max-w-[90vw] max-h-[90vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};
